import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { CatalogItem } from '../../../../redux/api/fymAPI.types';
import { WorkEntry } from './workListModel';
import { WorkListRow } from './WorkListRow';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Work.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface WorkSectionProps {
  testID: string;
  title: string;
  /** The class of the state pill (`pillBusy`, `pillFailed`, `pillDone`). */
  pillClass: string | undefined;
  /** The note on the right of the head ("Last 7 days"). */
  headNote?: string;
  /** The line of an empty section; with none, an empty section does not show. */
  emptyNote?: string;
  accountId: number;
  entries: WorkEntry[];
  songTitleOf: (catalogItemUuid: string | null) => string;
  catalogByUuid: ReadonlyMap<string, CatalogItem>;
  readOnly: boolean;
  linkedUuid: string;
  now: Date;
}

/**
 * One list of the Work page (design assets-rethink e1 `.wk-sec`): "In progress", "Failed" or
 * "Finished". It is the lane of Posts at full width: the state pill, the count, the rows.
 */
const WorkSectionComponent: React.FC<WorkSectionProps> = (props) => {
  const { testID, title, pillClass, headNote, emptyNote, accountId, entries } = props;
  const { songTitleOf, catalogByUuid, readOnly, linkedUuid, now } = props;
  const intl = useIntl();
  if (entries.length === 0 && emptyNote === undefined) {
    return null;
  }
  return (
    <section className={styles.section} data-testid={testID}>
      <div className={styles.sectionHead}>
        <h2 className={clsx(styles.pill, pillClass, 'm-0')}>{title}</h2>
        <span className={styles.eyebrow} data-testid="work-count">
          {intl.formatNumber(entries.length)}
        </span>
        <span className={styles.grow} />
        {headNote !== undefined && <span className={styles.eyebrow}>{headNote}</span>}
      </div>
      {entries.length === 0 ? (
        <p className={styles.quiet}>{emptyNote}</p>
      ) : (
        <ul className={styles.list}>
          {entries.map((entry) => (
            <WorkListRow
              key={entry.key}
              accountId={accountId}
              entry={entry}
              songTitle={songTitleOf(entry.catalogItemUuid)}
              catalogItem={entry.catalogItemUuid === null ? undefined : catalogByUuid.get(entry.catalogItemUuid)}
              readOnly={readOnly}
              isLinked={entry.uuid === linkedUuid}
              now={now}
            />
          ))}
        </ul>
      )}
    </section>
  );
};

export const WorkSection = TypedMemo(WorkSectionComponent);
